import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import type { ContactProfileData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPSERT_CONTACT_PROFILE = gql`
  mutation UpsertContactProfile(
    $contactId: ID!
    $values: ContactProfileUpsertInput!
  ) {
    upsertContactProfile(contactId: $contactId, values: $values) {
      ok
      created
      contactProfile {
        id
        confirmRemovePassword
        emailConfirmed
        localLoginDisabled
        lockoutEnabled
        loginEnabled
        mobilePhoneConfirmed
        twoFactorEnabled
        profileAlert
        profileIsAnonymous
      }
    }
  }
`;

const PROFILE_FIELDS: Record<string, { apiField: string }> = {
  confirmRemovePassword: { apiField: 'confirmRemovePassword' },
  emailConfirmed: { apiField: 'emailConfirmed' },
  localLoginDisabled: { apiField: 'localLoginDisabled' },
  lockoutEnabled: { apiField: 'lockoutEnabled' },
  loginEnabled: { apiField: 'loginEnabled' },
  mobilePhoneConfirmed: { apiField: 'mobilePhoneConfirmed' },
  profileAlert: { apiField: 'profileAlert' },
  profileIsAnonymous: { apiField: 'profileIsAnonymous' },
  twoFactorEnabled: { apiField: 'twoFactorEnabled' }
};

export interface EditContactProfileDialogProps {
  contactId: string;
  initialValue: ContactProfileData;
  isOpen: boolean;
  onChange: (data: ContactProfileData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactProfileData;
}

export function EditContactProfileDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditContactProfileDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertProfile] = useMutation(UPSERT_CONTACT_PROFILE, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactProfileData>(
    field: K,
    fieldValue: ContactProfileData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertProfile({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactProfile?: { ok?: boolean };
      };

      if (data.upsertContactProfile?.ok === false) {
        toast.error('Failed to save profile settings');
      } else {
        toast.success('Profile settings saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving profile settings:', error);
      toast.error('Failed to save profile settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Profile Settings"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditCheckboxField
          checked={value.confirmRemovePassword}
          label="Confirm Remove Password"
          onCheckedChange={v => {
            updateField('confirmRemovePassword', v);
          }}
        />
        <EditCheckboxField
          checked={value.emailConfirmed}
          label="Email Confirmed"
          onCheckedChange={v => {
            updateField('emailConfirmed', v);
          }}
        />
        <EditCheckboxField
          checked={value.localLoginDisabled}
          label="Local Login Disabled"
          onCheckedChange={v => {
            updateField('localLoginDisabled', v);
          }}
        />
        <EditCheckboxField
          checked={value.lockoutEnabled}
          label="Lockout Enabled"
          onCheckedChange={v => {
            updateField('lockoutEnabled', v);
          }}
        />
        <EditCheckboxField
          checked={value.loginEnabled}
          label="Login Enabled"
          onCheckedChange={v => {
            updateField('loginEnabled', v);
          }}
        />
        <EditCheckboxField
          checked={value.mobilePhoneConfirmed}
          label="Mobile Phone Confirmed"
          onCheckedChange={v => {
            updateField('mobilePhoneConfirmed', v);
          }}
        />
        <EditCheckboxField
          checked={value.twoFactorEnabled}
          label="Two Factor Enabled"
          onCheckedChange={v => {
            updateField('twoFactorEnabled', v);
          }}
        />
        <EditCheckboxField
          checked={value.profileAlert}
          label="Profile Alert"
          onCheckedChange={v => {
            updateField('profileAlert', v);
          }}
        />
        <EditCheckboxField
          checked={value.profileIsAnonymous}
          label="Profile Is Anonymous"
          onCheckedChange={v => {
            updateField('profileIsAnonymous', v);
          }}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactProfileData,
  initial: ContactProfileData
): Record<string, boolean> {
  const changes: Record<string, boolean> = {};

  for (const [uiField, config] of Object.entries(PROFILE_FIELDS)) {
    const currentVal = current[uiField as keyof ContactProfileData];
    const initialVal = initial[uiField as keyof ContactProfileData];

    if (currentVal !== initialVal) {
      changes[config.apiField] = currentVal;
    }
  }

  return changes;
}
